import React, { useState } from "react";
import AddFamilyMemberModal from "./AddFamilyMemberModal";
import UnlinkFamilyMemberModal from "./UnlinkFamilyMemberModal";
import { fetchClientById } from "../../store/thunks/clientsThunk";
import Table from "../../applicationUi/components/Table";
import { logInfo } from "../../utils/logger";
import { calculateAge } from "../../utils/calculateAge";
import { useDispatch, useSelector } from "react-redux";
import {
  UserGroupIcon,
  UserIcon,
  PlusIcon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon
} from "@heroicons/react/24/outline";

const FamilyDetails = ({ display }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState({
    type: "",
    open: false,
    action: "",
  });
  const client = useSelector((state) => state.clients.selectedClient);
  console.log("client", client);

  // Utility function to format phone numbers
  const formatPhoneNumbers = (phoneNumbers) => {
    if (!phoneNumbers || !Array.isArray(phoneNumbers)) {
      return "No phone numbers available";
    }
    return phoneNumbers
      .map(
        (phone) =>
          `${phone.type}: ${phone.number}${phone.note ? ` ${phone.note}` : ""}`
      )
      .join(", ");
  };

  // Format family member data to fit table structure
  const formatFamilyMember = (member) => {
    return {
      _id: member._id,
      firstName: member.firstName,
      lastName: member.lastName,
      dateOfBirth: member.dateOfBirth
        ? new Date(member.dateOfBirth).toLocaleDateString()
        : "N/A",
      age: calculateAge(member.dateOfBirth),
      email: member.email,
      formattedPhoneNumbers: formatPhoneNumbers(member.phoneNumbers),
    };
  };

  const parents =
    client.parents?.map((parent) => formatFamilyMember(parent)) || [];
  const children =
    client.children?.map((child) => formatFamilyMember(child)) || [];

  const handleOpenModal = (type, action) => {
    setShowModal({ type, open: true, action });
    logInfo(`Modal opened for ${action}ing ${type}`, { clientId: client._id });
  };

  const handleCloseModal = () => {
    setShowModal({ type: "", open: false, action: "" });
    logInfo("Modal closed", { clientId: client._id });
  };

  const handleLinkParent = () => {
    console.log("test - link parent clicked");
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <UserGroupIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Family Details</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage family relationships and contact information
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Parents Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <UserIcon className="h-5 w-5 text-gray-500" />
              <h2 className="text-lg font-medium text-gray-900">Parents</h2>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {parents.length} {parents.length === 1 ? 'parent' : 'parents'}
              </span>
            </div>
            {!display && (
              <div className="flex space-x-2">
                <button
                  onClick={() => handleOpenModal("parent", "add")}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Parent
                </button>
                {parents.length > 0 && (
                  <button
                    onClick={() => handleOpenModal("parent", "unlink")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Unlink
                  </button>
                )}
              </div>
            )}
          </div>

          {parents.length > 0 ? (
            <div className="bg-gray-50 rounded-lg p-1">
              <Table
                columns={[
                  {
                    header: "First Name",
                    accessor: "firstName",
                    render: (row) => (
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <UserIcon className="h-4 w-4 text-blue-600" />
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {row.firstName}
                          </div>
                        </div>
                      </div>
                    )
                  },
                  {
                    header: "Last Name",
                    accessor: "lastName",
                    render: (row) => (
                      <span className="text-sm text-gray-900">{row.lastName}</span>
                    )
                  },
                  {
                    header: "Age",
                    accessor: "age",
                    render: (row) => (
                      <div className="flex items-center space-x-1">
                        <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{row.age} years</span>
                      </div>
                    )
                  },
                  { 
                    header: "Email", 
                    accessor: "email",
                    render: (row) => row.email ? (
                      <div className="flex items-center space-x-1">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{row.email}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">No email</span>
                    )
                  },
                  { 
                    header: "Phone", 
                    accessor: "formattedPhoneNumbers",
                    render: (row) => (
                      <div className="flex items-center space-x-1">
                        <PhoneIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{row.formattedPhoneNumbers}</span>
                      </div>
                    )
                  },
                ]}
                data={parents}
                onRowClick={(row) => console.log("Parent row clicked:", row)}
                showPagination={false}
                className="bg-white"
              />
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No parents linked</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by linking or adding a parent to this client's profile.
              </p>
              {!display && (
                <div className="mt-4">
                  <button
                    onClick={() => handleOpenModal("parent", "add")}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Parent
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Children Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <UserIcon className="h-5 w-5 text-gray-500" />
              <h2 className="text-lg font-medium text-gray-900">Children</h2>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {children.length} {children.length === 1 ? 'child' : 'children'}
              </span>
            </div>
            {!display && (
              <div className="flex space-x-2">
                <button
                  onClick={() => handleOpenModal("child", "add")}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Child
                </button>
                {children.length > 0 && (
                  <button
                    onClick={() => handleOpenModal("child", "unlink")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Unlink
                  </button>
                )}
              </div>
            )}
          </div>

          {children.length > 0 ? (
            <div className="bg-gray-50 rounded-lg p-1">
              <Table
                columns={[
                  {
                    header: "First Name",
                    accessor: "firstName",
                    render: (row) => (
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                            <UserIcon className="h-4 w-4 text-green-600" />
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {row.firstName}
                          </div>
                        </div>
                      </div>
                    )
                  },
                  {
                    header: "Last Name",
                    accessor: "lastName",
                    render: (row) => (
                      <span className="text-sm text-gray-900">{row.lastName}</span>
                    )
                  },
                  {
                    header: "Age",
                    accessor: "age",
                    render: (row) => (
                      <div className="flex items-center space-x-1">
                        <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{row.age} years</span>
                      </div>
                    )
                  },
                  { 
                    header: "Email", 
                    accessor: "email",
                    render: (row) => row.email ? (
                      <div className="flex items-center space-x-1">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{row.email}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">No email</span>
                    )
                  },
                  { 
                    header: "Phone", 
                    accessor: "formattedPhoneNumbers",
                    render: (row) => (
                      <div className="flex items-center space-x-1">
                        <PhoneIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{row.formattedPhoneNumbers}</span>
                      </div>
                    )
                  },
                ]}
                data={children}
                onRowClick={(row) => console.log("Child row clicked:", row)}
                showPagination={false}
                className="bg-white"
              />
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No children linked</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by linking or adding a child to this client's profile.
              </p>
              {!display && (
                <div className="mt-4">
                  <button
                    onClick={() => handleOpenModal("child", "add")}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Child
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal for adding family members */}
      {showModal.open && showModal.action === "add" && (
        <AddFamilyMemberModal
          memberType={showModal.type}
          clientId={client._id}
          onClose={handleCloseModal}
          onCancel={handleCloseModal}
          onSave={handleLinkParent}
          onUpdateClient={() => {
            console.log("Client updated!");
            dispatch(fetchClientById(client._id));
          }}
        />
      )}

      {/* Modal for unlinking family members */}
      {showModal.open && showModal.action === "unlink" && (
        <UnlinkFamilyMemberModal
          memberType={showModal.type}
          clientId={client._id}
          familyMembers={showModal.type === "parent" ? parents : children}
          onClose={handleCloseModal}
          onCancel={handleCloseModal}
          onUnlink={() => {
            console.log("Family member unlinked");
          }}
          onUpdateClient={() => {
            console.log("Client updated!");
            return dispatch(fetchClientById(client._id));
          }}
        />
      )}
    </div>
  );
};

export default FamilyDetails;
